import React, { FC } from "react";
import { Box } from "@mui/material";
import { Img } from "../Img";
import { formatImgUrl } from "utils/helpers";
import { PLAN_NAMES } from "constants/data";
import useMediaBreakpoints from "hooks/useMediaBreakpoints";

interface GalleryDisplayProps {
  medias: string[];
  destinationPath: string;
  maxHeight?: number;
  planName?: string;
  isProduct?: boolean;
  productPath?: string;
}

const GalleryDisplay: FC<GalleryDisplayProps> = ({
  medias,
  destinationPath,
  maxHeight,
  planName,
  isProduct = false,
  productPath,
}) => {
  const { isMobile } = useMediaBreakpoints();
  // Determine number of images to show based on plan
  const maxImages = planName === PLAN_NAMES.advance ? 4 : 3;
  const displayMedias = medias.slice(0, maxImages);

  // Set maxHeight based on plan if not provided
  let computedMaxHeight = maxHeight;
  if (!maxHeight) {
    if (isMobile && planName !== PLAN_NAMES.advance && displayMedias.length === 3) {
      computedMaxHeight = 160; // Starter mobile 3 ảnh: nhỏ hơn Superior
    } else {
      computedMaxHeight = planName === PLAN_NAMES.advance ? 250 : 200;
    }
  }
  if (!computedMaxHeight) computedMaxHeight = 200;

  // Special mobile starter layout
  const isMobileStarter = isMobile && planName !== PLAN_NAMES.advance && displayMedias.length === 3;

  // Determine link for main image (if product, use productPath)
  const mainImageLink = isProduct && productPath ? productPath : destinationPath;

  // Xác định có phải link nội bộ không (bắt đầu bằng /)
  const isInternal = mainImageLink && mainImageLink.startsWith("/");

  // Handler cho internal link nếu muốn chuyển hướng bằng JS (nếu cần)
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isInternal) {
      // Nếu dùng react-router, có thể dùng navigate ở đây
      // e.preventDefault();
      // navigate(mainImageLink);
    }
  };

  return (
    <a
      href={mainImageLink}
      onClick={handleClick}
      style={{
        display: "block",
        height: "100%",
        width: "100%",
        textDecoration: "none",
      }}
      {...(isInternal ? { rel: "noopener noreferrer" } : { target: "_blank", rel: "noopener noreferrer" })}
    >
      {isMobileStarter ? (
        // Mobile 3-image layout (1 big + 2 small stacked)
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            maxHeight: `${computedMaxHeight}px`,
            gap: "2px",
            overflow: "hidden",
          }}
        >
          {/* Left: big image */}
          <Box
            sx={{
              width: "66.66%",
              height: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Img
              src={formatImgUrl(displayMedias[0])}
              alt="main-media"
              fillContainer
            />
          </Box>
          
          {/* Right: 2 stacked images */}
          <Box
            sx={{
              width: "33.33%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {[displayMedias[1], displayMedias[2]].map((media, idx) => (
              <Box
                key={idx}
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Img
                  src={formatImgUrl(media)}
                  alt={`media-${idx + 2}`}
                  fillContainer
                />
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        // Standard layout
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            maxHeight: `${computedMaxHeight}px`,
            flexDirection: "column",
            gap: "2px",
            overflow: "hidden",
          }}
        >
          {displayMedias.length === 1 ? (
            // Single image layout
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Img
                src={formatImgUrl(displayMedias[0])}
                alt="main-media"
                fillContainer
              />
            </Box>
          ) : (
            // Multiple images layout
            <>
              {/* Top image - takes 2/3 height */}
              <Box
                sx={{
                  height: "66.66%",
                  width: "100%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {displayMedias.length > 0 && (
                  <Img
                    src={formatImgUrl(displayMedias[0])}
                    alt="main-media"
                    fillContainer
                  />
                )}
              </Box>

              {/* Bottom row with remaining images */}
              <Box
                sx={{
                  height: "33.33%",
                  width: "100%",
                  display: "flex",
                  gap: "2px",
                }}
              >
                {displayMedias.slice(1).map((media, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      height: "100%",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Img
                      src={formatImgUrl(media)}
                      alt={`media-${i + 1}`}
                      fillContainer
                    />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </a>
  );
};

export default GalleryDisplay;
